import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { showPopup } from '../../common/popups/actions'
import { Button, CreateOrderButtonsWrapper, Link, Row } from '../components'
import { TextField, SwitchField } from '../components/fields'
import { WrappedLoading } from '../components/Loading'
import ErrorTryAgain from '../components/ErrorTryAgain'
import {
  CART_META_MAX_LENGTH,
  isBulkOrderRemarkEnabled,
  parseEditableCartQuantity,
  removeBulkOrderCartLine,
  removeBulkOrderCartLinesByLensId,
  selectCartLineCount,
  selectCartLineGroups,
  selectCartReference,
  selectCartRemark,
  selectCartTotalQuantity,
  selectGridContext,
  selectHasCartError,
  selectIsCartEditable,
  selectIsCartLoading,
  setBulkOrderCartMeta,
  setBulkOrderGridContext,
  updateCartLineQuantity,
} from '../../common/bulkOrder'
import BulkOrderCartProductGroup from './BulkOrderCartProductGroup'
import BulkOrderCountSummary from './BulkOrderCountSummary'
import BulkOrderPageShell from './BulkOrderPageShell'
import BulkOrderSteps from './BulkOrderSteps'
import useBulkOrderCartActions from './useBulkOrderCartActions'
import useRetryBulkOrderCartFetch from './useRetryBulkOrderCartFetch'
import { SIGN_SWITCH_OPTIONS } from './config'
import {
  CartBox,
  CartContent,
  CartGroupList,
  CartHeader,
  CartMetaActions,
  CartMetaCylinderSign,
  CartMetaField,
  CartMetaFields,
  CartMetaFieldsGroup,
  CartSummary,
  CartSummaryText,
  EmptyState,
  RemoveProductConfirmMessage,
  RemoveProductConfirmProductLine,
} from './BulkOrderCartPage.styles'

const BulkOrderCartPageContent = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const cartGroups = useSelector(selectCartLineGroups)
  const cartLineCount = useSelector(selectCartLineCount)
  const cartTotalQuantity = useSelector(selectCartTotalQuantity)
  const cartReference = useSelector(selectCartReference)
  const cartRemark = useSelector(selectCartRemark)
  const { cylinderSign } = useSelector(selectGridContext)
  const isCartLoading = useSelector(selectIsCartLoading)
  const hasCartError = useSelector(selectHasCartError)
  const isCartEditable = useSelector(selectIsCartEditable)

  const {
    handleEmptyCart,
    handleSend,
    isEmptyCartDisabled,
    isSendDisabled,
  } = useBulkOrderCartActions()
  const handleRetryCart = useRetryBulkOrderCartFetch()

  const handleReferenceChange = useCallback(
    target => {
      if (!isCartEditable) return
      dispatch(setBulkOrderCartMeta({ reference: target.value }))
    },
    [dispatch, isCartEditable],
  )

  const handleRemarkChange = useCallback(
    target => {
      if (!isCartEditable) return
      dispatch(setBulkOrderCartMeta({ remark: target.value }))
    },
    [dispatch, isCartEditable],
  )

  const handleQuantityChange = useCallback(
    (line, value) => {
      if (!isCartEditable) return
      const quantity = parseEditableCartQuantity(value, line.quantity)
      if (quantity == null) return

      dispatch(updateCartLineQuantity({ id: line.id, quantity }))
    },
    [dispatch, isCartEditable],
  )

  const handleRemoveLine = useCallback(
    id => {
      if (!isCartEditable) return
      dispatch(removeBulkOrderCartLine({ id }))
    },
    [dispatch, isCartEditable],
  )

  const handleRemoveProduct = useCallback(
    group => {
      if (!isCartEditable) return
      dispatch(
        showPopup('confirm', {
          title: t('Remove product?'),
          text: (
            <>
              <RemoveProductConfirmProductLine>
                {group.lensName} · {group.totalQuantity} {t('pieces')}
              </RemoveProductConfirmProductLine>
              <RemoveProductConfirmMessage>
                {t('BULK_ORDER_REMOVE_PRODUCT_WARNING')}
              </RemoveProductConfirmMessage>
            </>
          ),
          okText: t('remove'),
          cancelText: t('cancel'),
          hideCancel: false,
          onConfirm: () => dispatch(removeBulkOrderCartLinesByLensId({ lensId: group.lensId })),
        }),
      )
    },
    [dispatch, isCartEditable, t],
  )

  return (
    <>
      <CartBox>
        <CartHeader>
          <BulkOrderSteps activeStep={2} cartLineCount={cartLineCount} />
        </CartHeader>

        <CartContent>
          {isCartLoading && <WrappedLoading type="puff" padding="2rem" />}

          {hasCartError && !isCartLoading && <ErrorTryAgain onClick={handleRetryCart} />}

          {!isCartLoading && !hasCartError && (
            <>
              <CartMetaFields>
                <CartMetaFieldsGroup>
                  <CartMetaField>
                    <TextField
                      name="bulkOrderCartReference"
                      label={t('reference')}
                      value={cartReference}
                      maxLength={CART_META_MAX_LENGTH}
                      disabled={!isCartEditable}
                      onChange={handleReferenceChange}
                    />
                  </CartMetaField>
                  {isBulkOrderRemarkEnabled() && (
                    <CartMetaField>
                      <TextField
                        name="bulkOrderCartRemark"
                        label={t('BULK_ORDER_REMARK_FOR_MANUFACTURER')}
                        value={cartRemark}
                        maxLength={CART_META_MAX_LENGTH}
                        disabled={!isCartEditable}
                        onChange={handleRemarkChange}
                      />
                    </CartMetaField>
                  )}
                </CartMetaFieldsGroup>
                <CartMetaCylinderSign>
                  <SwitchField
                    name="bulkOrderCartCylinderSign"
                    label={t('cylinder')}
                    value={cylinderSign}
                    options={SIGN_SWITCH_OPTIONS}
                    required
                    hideRequiredStar
                    onChange={({ value }) =>
                      dispatch(setBulkOrderGridContext({ cylinderSign: value }))
                    }
                  />
                </CartMetaCylinderSign>
                <CartMetaActions>
                  <Button
                    primary
                    rightIcon="CustomDoubleArrow"
                    disabled={isSendDisabled}
                    onClick={handleSend}
                  >
                    {t('send')}
                  </Button>
                </CartMetaActions>
              </CartMetaFields>

              {cartGroups.length === 0 
                ? <EmptyState>{t('BULK_ORDER_CART_EMPTY')}</EmptyState>
               : (
                <>
                  <CartGroupList>
                    {cartGroups.map(group => (
                      <BulkOrderCartProductGroup
                        key={group.lensId}
                        group={group}
                        defaultExpanded={false}
                        disabled={!isCartEditable}
                        onQuantityChange={handleQuantityChange}
                        onRemoveLine={handleRemoveLine}
                        onRemoveProduct={handleRemoveProduct}
                      />
                    ))}
                  </CartGroupList>
                  <CartSummary>
                    <CartSummaryText>
                      <BulkOrderCountSummary
                        productCount={cartGroups.length}
                        variantCount={cartLineCount}
                        pieceCount={cartTotalQuantity}
                      />
                    </CartSummaryText>
                  </CartSummary>
                </>
              )}
            </>
          )}
        </CartContent>
      </CartBox>

      <CreateOrderButtonsWrapper>
        <Row alignItems="stretch">
          <Button.StepBack to={Link.BULK_ORDER}>{t('catalog')}</Button.StepBack>
        </Row>
        <Row alignItems="stretch">
          <Button
            large
            leftIcon="IosTrash"
            iconSize={28}
            maxWidth="20rem"
            disabled={isEmptyCartDisabled}
            onClick={handleEmptyCart}
          >
            {t('empty cart')}
          </Button>
        </Row>
        <Row justifyContent="flex-end" alignItems="stretch">
          <Button
            large
            primary
            rightIcon="CustomDoubleArrow"
            maxWidth="20rem"
            disabled={isSendDisabled}
            onClick={handleSend}
          >
            {t('send')}
          </Button>
        </Row>
      </CreateOrderButtonsWrapper>
    </>
  )
}

const BulkOrderCartPage = () => {
  const { t } = useTranslation()

  return (
    <BulkOrderPageShell metaTitle={t('Bulk order cart')}>
      <BulkOrderCartPageContent />
    </BulkOrderPageShell>
  )
}

export default BulkOrderCartPage
